import React, {Component} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {markFullStorySensitive} from '../../../../../../helpers/fullstoryHelpers';
import {ChannelModel} from '../../../../../../models/api/channelModel';
import {
  LoadedMessageModel,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
} from '../../../../../../models/api/messageModel';
import {EmlPanelId, MessagePanelId, ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../store/timelineVariations/timelineVariationSelectors';
import {MessageViewerActionReply} from '../action/messageViewerActionReply';
import {MessageViewerActionZone} from '../action/messageViewerActionZone';
import {MessageViewerAttachmentsContainer} from '../messageViewerAttachmentsContainer';
import {MessageViewerAvatar, MessageViewerAvatarProps} from '../messageViewerAvatar';
import {MessageViewerChatSeen} from '../messageViewerChatSeen';
import {MessageViewerDate, MessageViewerDateProps} from '../messageViewerDate';
import {MessageViewerDetails} from '../messageViewerDetails';
import {MessageViewerMoreBaseProps} from '../messageViewerMore';
import {MessageViewerMoreContainer} from '../messageViewerMoreContainer';
import {MessageViewerSeen} from '../messageViewerSeen';
import {MessageViewerVisitorUrl} from '../messageViewerVisitorUrl';
import {MessageViewerSender, MessageViewerSenderProps} from '../sender/messageViewerSender';
import {MessageViewerChatBody, MessageViewerChatBodyProps} from './messageViewerChatBody';

/*
 * Props.
 */

interface MessageViewerChatProps
  extends MessageViewerAvatarProps,
    MessageViewerChatBodyProps,
    MessageViewerMoreBaseProps,
    Omit<MessageViewerSenderProps, 'message'>,
    Omit<MessageViewerDateProps, 'message'> {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  message: LoadedMessageModel;
  replyChannel?: ChannelModel;
  isChatFollowUp?: boolean;
  isInMessagePopup: boolean;
  hasNewVisitorUrlExperience?: boolean;
  chatUserName?: string;
  timelineVariation: TimelineVariationsEnum;
  isInComposerOnBottom?: boolean;
  composerShouldHaveMaxHeight: boolean;
}

interface MessageViewerChatState {
  hasVisitorUrlAndMoreHover: boolean;
}

interface StyledChatDivProps {
  $isVisitorUrlIconVisible?: boolean;
  $timelineVariation: TimelineVariationsEnum;
  $isInComposerOnBottom?: boolean;
}

/*
 * Style.
 */

interface StyledVisitorMoreWrapperDivProps {
  $isChatFollowup?: boolean;
  $hasHover: boolean;
}
const StyledVisitorMoreWrapperDiv = styled.div<StyledVisitorMoreWrapperDivProps>`
  grid-area: visitor-more;
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  position: relative;
  height: 1px;
  overflow: visible;

  & > div {
    margin-right: -76px;
    padding-left: 16px;

    width: fit-content;
    height: auto;

    display: flex;
    flex-direction: row;

    opacity: ${(p) => (p.$hasHover ? '1' : '0')};

    &:hover {
      opacity: 1;
    }
  }
`;

const StyledChatDiv = styled.div<StyledChatDivProps>`
  display: grid;
  grid-template-rows: auto auto;
  /*  */

  &:hover ${StyledVisitorMoreWrapperDiv} {
    & > div {
      opacity: 1;
    }
  }

  ${(p) =>
    addChatDivStyles(p.$timelineVariation, Boolean(p.$isVisitorUrlIconVisible), p.$isInComposerOnBottom)}
`;

interface StyledAvatarDivProps {
  $timelineVariation: TimelineVariationsEnum;
}
const StyledAvatarDiv = styled.div<StyledAvatarDivProps>`
  grid-area: avatar;
  align-self: start;

  ${(p) => addAvatarStyles(p.$timelineVariation)}
`;

interface RecipientsStyleProps {
  $hasManyRecipients?: boolean;
  $timelineVariation: TimelineVariationsEnum;
}
const StyledRecipientsDiv = styled.div<RecipientsStyleProps>`
  grid-area: recipient;
  overflow: hidden;

  display: flex;

  ${(p) => addTimelineVariationRecipientsStyles(p.$timelineVariation)}

  ${(p) => maybeAddMarginStyles(p.$hasManyRecipients)};
`;

const addTimelineVariationRecipientsStyles = ($timelineVariation: TimelineVariationsEnum) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
    ? css`
        margin: -37px 7px 5px -8px;
        position: relative;
        width: 1px;
        overflow: visible;
      `
    : css`
        margin: 5px 7px;
      `;

function maybeAddMarginStyles(hasManyRecipients?: boolean) {
  if (hasManyRecipients) {
    return css`
      margin-top: 0;
      margin-left: 0;
    `;
  }

  return '';
}

interface ContentDivStyleProps {
  $timelineVariation: TimelineVariationsEnum;
  $composerShouldHaveMaxHeight?: boolean;
}
const StyledContentDiv = styled.div<ContentDivStyleProps>`
  grid-area: content;
  align-self: start;
  /* Allow this grid item to shrink and overflow wrap. */
  min-width: 0;

  ${(p) => addContentDivStyles(p.$timelineVariation, p.$composerShouldHaveMaxHeight)}
`;
const addContentDivStyles = (
  $timelineVariation: TimelineVariationsEnum,
  $composerShouldHaveMaxHeight?: boolean,
) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
    ? css`
        margin: -8px;
        min-height: 36px;
      `
    : css`
        margin: -4px -9px -7px -9px;

        ${$composerShouldHaveMaxHeight &&
        css`
          /* We want to give the timeline at least 700px of real estate, and the draft preview 100px. */
          max-height: max(calc(100vh - 700px), 100px);
          overflow-y: auto;
        `}
      `;

interface StyledFollowUpChatDivProps {
  $timelineVariation: TimelineVariationsEnum;
}
const StyledFollowUpChatDiv = styled.div<StyledFollowUpChatDivProps>`
  display: grid;
  grid-template-columns: 1fr;

  &:hover ${StyledVisitorMoreWrapperDiv} {
    & > div {
      opacity: 1;
    }
  }

  ${(p) => addFollowUpChatStyles(p.$timelineVariation)}
`;
const addFollowUpChatStyles = ($timelineVariation: TimelineVariationsEnum) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
    ? css`
        grid-template-areas: 'content visitor-more seen-date';
        align-items: start;
        padding: 8px;

        & ${StyledVisitorMoreWrapperDiv} {
          & > div {
            margin-top: -4px;
          }
        }
      `
    : css`
        position: relative;
        grid-template-areas: 'content';
        align-items: center;
        padding: 7px 9px 7px 53px;
      `;

interface StyledFollowUpContentDivProps {
  $timelineVariation: TimelineVariationsEnum;
}
const StyledFollowUpContentDiv = styled(StyledContentDiv)<StyledFollowUpContentDivProps>`
  /* Allow this grid item to shrink and overflow wrap. */
  min-width: 0;

  ${(p) => addFollowUpContentDivStyles(p.$timelineVariation)}
`;
const addFollowUpContentDivStyles = ($timelineVariation: TimelineVariationsEnum) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
    ? css`
        margin: -8px;
        min-height: 36px;
      `
    : css`
        margin: -7px -9px;
      `;

/* Base container for message date and more menu button. */
const StyledDetailsBaseDiv = styled.div`
  display: flex;
  align-items: center;
`;

/* Floating variation to make the message content wrap. */
const StyledDetailsSpacerDiv = styled(StyledDetailsBaseDiv)`
  float: right;
  margin-top: 6px;
  margin-right: 9px;
  visibility: hidden;
  pointer-events: none;
`;

/* Absolutely positioned variation to show over top of the message. */
const StyledDetailsDiv = styled(StyledDetailsBaseDiv)`
  position: absolute;
  top: 6px;
  right: 9px;
  transform: translateY(0.5px);
`;

interface StyledMessageViewerChatBodyProps {
  $timelineVariation: TimelineVariationsEnum;
  $isDraft: boolean;
}
const StyledMessageViewerChatBody = styled(MessageViewerChatBody)<StyledMessageViewerChatBodyProps>`
  ${(p) => addMessageViewerChatBodyStyles(p.$timelineVariation, p.$isDraft)}
`;
const addMessageViewerChatBodyStyles = ($timelineVariation: TimelineVariationsEnum, isDraft: boolean) =>
  $timelineVariation === TimelineVariationsEnum.ASYNCHRONOUS &&
  !isDraft &&
  css`
    padding-top: 0;
  `;

const StyledSeenDateWrapperDiv = styled.div`
  grid-area: seen-date;

  & > div {
    position: absolute;
    right: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

/*
 * Component.
 */

export class MessageViewerChat extends Component<MessageViewerChatProps, MessageViewerChatState> {
  constructor(props: MessageViewerChatProps) {
    super(props);

    this.state = {
      hasVisitorUrlAndMoreHover: false,
    };

    this.setHasVisitorUrlAndMoreHover = this.setHasVisitorUrlAndMoreHover.bind(this);
  }

  render() {
    const messageHasBody = (this.props.message.body || '').trim().length > 0;

    return this.props.isChatFollowUp
      ? this.renderFollowUpMessage(messageHasBody)
      : this.renderFullMessage(messageHasBody);
  }

  private setHasVisitorUrlAndMoreHover(newHasVisitorUrlAndMoreHover: boolean) {
    this.setState({hasVisitorUrlAndMoreHover: newHasVisitorUrlAndMoreHover});
  }

  private renderDetails() {
    const {message, visualRecipients} = this.props;
    const {to} = visualRecipients;

    // If this is SMS or if we have multiple "to" recipients, show all of them.
    const isSms = message.type === MessageTypesEnum.SMS;

    if ((to && to.length > 1) || isSms) {
      return <MessageViewerDetails {...this.props} searchQuery={this.props.cellsContext.searchQuery} />;
    }

    // Otherwise, just show the sender.
    return <MessageViewerSender {...this.props} isClickable needsParentHover />;
  }

  private renderFullMessage(messageHasBody?: boolean) {
    const {to} = this.props.visualRecipients;
    const hasManyToRecipients = to && to.length > 1;

    const layerExtra =
      this.props.message.visualStatus === MessageVisualStatusesEnum.TIMELINE && this.props.message.layerExtra;

    const isDraft = this.props.message.visualStatus === MessageVisualStatusesEnum.DRAFT;

    return (
      <>
        <StyledChatDiv
          className={buildHoverParentClassName()}
          $isVisitorUrlIconVisible={this.props.message.isInbound && this.props.hasNewVisitorUrlExperience}
          $timelineVariation={this.props.timelineVariation}
        >
          {!this.props.isInComposerOnBottom && (
            <StyledAvatarDiv $timelineVariation={this.props.timelineVariation}>
              {<MessageViewerAvatar {...this.props} isChatAvatar />}
            </StyledAvatarDiv>
          )}
          <StyledRecipientsDiv
            $hasManyRecipients={hasManyToRecipients}
            $timelineVariation={this.props.timelineVariation}
          >
            {this.renderDetails()}
          </StyledRecipientsDiv>
          <StyledContentDiv
            className={markFullStorySensitive()}
            $timelineVariation={this.props.timelineVariation}
            $composerShouldHaveMaxHeight={this.props.composerShouldHaveMaxHeight}
          >
            {renderAttachments(this.props)}
            {messageHasBody && (
              <StyledMessageViewerChatBody
                {...this.props}
                $timelineVariation={this.props.timelineVariation}
                $isDraft={isDraft}
              />
            )}
          </StyledContentDiv>
          {renderSeenAndDate(this.props)}
          {renderVisitorUrlAndMore(
            this.props,
            layerExtra,
            this.state.hasVisitorUrlAndMoreHover,
            this.setHasVisitorUrlAndMoreHover,
          )}
        </StyledChatDiv>
        {this.props.replyChannel && renderReplyAction(this.props)}
      </>
    );
  }

  private renderFollowUpMessage(messageHasBody?: boolean) {
    const layerExtra =
      this.props.message.visualStatus === MessageVisualStatusesEnum.TIMELINE && this.props.message.layerExtra;
    return (
      <>
        <StyledFollowUpChatDiv
          className={buildHoverParentClassName()}
          $timelineVariation={this.props.timelineVariation}
        >
          <StyledFollowUpContentDiv $timelineVariation={this.props.timelineVariation}>
            {this.props.timelineVariation === TimelineVariationsEnum.ASYNCHRONOUS && (
              <StyledDetailsSpacerDiv>
                {maybeRenderSeen(this.props)}
                <MessageViewerDate
                  message={this.props.message}
                  isInDragRegion={this.props.isInDragRegion}
                  timelineVariation={this.props.timelineVariation}
                />
                <MessageViewerMoreContainer {...this.props} />
              </StyledDetailsSpacerDiv>
            )}
            {renderAttachments(this.props)}
            {messageHasBody && <MessageViewerChatBody {...this.props} />}
          </StyledFollowUpContentDiv>
          {this.props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS &&
            renderSeenAndDate(this.props)}
          {renderDetailsByTimelineVariation(
            this.props,
            layerExtra,
            this.state.hasVisitorUrlAndMoreHover,
            this.setHasVisitorUrlAndMoreHover,
          )}
        </StyledFollowUpChatDiv>
        {this.props.replyChannel && renderReplyAction(this.props)}
      </>
    );
  }
}

function maybeRenderSeen(props: MessageViewerChatProps) {
  if (props.message.type !== MessageTypesEnum.FRONT_CHAT) {
    return <MessageViewerSeen message={props.message} isInDragRegion={props.isInDragRegion} />;
  }

  if (!props.message.isInbound && props.message.isSeen) {
    return <MessageViewerChatSeen chatUserName={props.chatUserName} message={props.message} />;
  }

  return null;
}

function renderReplyAction(props: Readonly<MessageViewerChatProps>) {
  return (
    <MessageViewerActionZone isInPopup={props.isInMessagePopup}>
      <MessageViewerActionReply
        iconName="reply"
        onClick={props.onReplyClick}
        messageType={props.message.type}
      />
    </MessageViewerActionZone>
  );
}

function renderAttachments(props: Readonly<MessageViewerChatProps>) {
  return (
    <MessageViewerAttachmentsContainer message={props.message} timelineVariation={props.timelineVariation} />
  );
}

function renderSeenAndDate(props: Readonly<MessageViewerChatProps>) {
  const seenAndDate = (
    <>
      {maybeRenderSeen(props)}
      <MessageViewerDate
        message={props.message}
        isInDragRegion={props.isInDragRegion}
        timelineVariation={props.timelineVariation}
      />
    </>
  );

  return props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS ? (
    <StyledSeenDateWrapperDiv>
      <div>{seenAndDate}</div>
    </StyledSeenDateWrapperDiv>
  ) : (
    seenAndDate
  );
}

function renderVisitorUrlAndMore(
  props: Readonly<MessageViewerChatProps>,
  layerExtra: any,
  hasVisitorUrlAndMoreHover: boolean,
  setHasVisitorUrlAndMoreHover: HandlerOf<boolean>,
) {
  const visitorUrlAndMore = (
    <>
      {props.message.isInbound && props.hasNewVisitorUrlExperience && layerExtra && (
        <MessageViewerVisitorUrl
          url={layerExtra.layerPageUrl}
          onPopoverToggle={setHasVisitorUrlAndMoreHover}
        />
      )}
      <MessageViewerMoreContainer {...props} onPopoverToggle={setHasVisitorUrlAndMoreHover} />
    </>
  );

  return props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS ? (
    <StyledVisitorMoreWrapperDiv $isChatFollowup={props.isChatFollowUp} $hasHover={hasVisitorUrlAndMoreHover}>
      <div>{visitorUrlAndMore}</div>
    </StyledVisitorMoreWrapperDiv>
  ) : (
    visitorUrlAndMore
  );
}

const renderDetailsByTimelineVariation = (
  props: Readonly<MessageViewerChatProps>,
  layerExtra: any,
  hasVisitorUrlAndMoreHover: boolean,
  setHasVisitorUrlAndMoreHover: HandlerOf<boolean>,
) =>
  props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS ? (
    <StyledVisitorMoreWrapperDiv $isChatFollowup={props.isChatFollowUp} $hasHover={hasVisitorUrlAndMoreHover}>
      <div>
        {props.message.isInbound && props.hasNewVisitorUrlExperience && layerExtra && (
          <MessageViewerVisitorUrl
            url={layerExtra.layerPageUrl}
            onPopoverToggle={setHasVisitorUrlAndMoreHover}
          />
        )}
        <MessageViewerMoreContainer {...props} onPopoverToggle={setHasVisitorUrlAndMoreHover} />
      </div>
    </StyledVisitorMoreWrapperDiv>
  ) : (
    <StyledDetailsDiv>
      {maybeRenderSeen(props)}
      <MessageViewerDate
        message={props.message}
        isInDragRegion={props.isInDragRegion}
        timelineVariation={props.timelineVariation}
      />
      {props.message.isInbound && props.hasNewVisitorUrlExperience && layerExtra && (
        <MessageViewerVisitorUrl url={layerExtra.layerPageUrl} />
      )}
      <MessageViewerMoreContainer {...props} />
    </StyledDetailsDiv>
  );

/*
 * Helpers.
 */

function addChatDivStyles(
  $timelineVariation: TimelineVariationsEnum,
  $isVisitorUrlIconVisible?: boolean,
  $isInComposerOnBottom?: boolean,
) {
  if ($timelineVariation === TimelineVariationsEnum.SYNCHRONOUS) {
    // Do not render the avatar if the message viewer is in the bottom with the composer
    if ($isInComposerOnBottom) {
      return css`
        grid-template-columns: 1fr auto auto auto;
        grid-template-areas:
          'recipient recipient recipient recipient'
          'content content content content';
      `;
    }

    return css`
      padding: 8px;
      grid-template-columns: auto 1fr auto auto;
      grid-template-areas:
        'recipient recipient recipient recipient'
        'avatar content visitor-more seen-date';

      & ${StyledVisitorMoreWrapperDiv} {
        & > div {
          margin-top: -4px;
        }
      }
    `;
  }

  return $isVisitorUrlIconVisible
    ? css`
        align-items: center;
        padding: 8px 9px;
        grid-template-columns: auto 1fr auto auto auto auto;
        grid-template-areas:
          'avatar recipient seen date visitor more'
          'avatar content content content content content';
      `
    : css`
        align-items: center;
        padding: 8px 9px;
        grid-template-columns: auto 1fr auto auto auto;
        grid-template-areas:
          'avatar recipient seen date more'
          'avatar content content content content';
      `;
}

const addAvatarStyles = ($timelineVariation: TimelineVariationsEnum) =>
  $timelineVariation === TimelineVariationsEnum.SYNCHRONOUS &&
  css`
    position: absolute;
    left: 0;
    margin-top: -13px;
  `;
